/*
 * Copyright (C) 2026 Discorch Contributors
 *
 * This file is part of Discorch.
 *
 * Discorch is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * Discorch is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with Discorch. If not, see <https://www.gnu.org/licenses/>.
 */

import { CopyButton } from "@/components/CopyButton";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useCopyToClipboard } from "@/hooks/useCopyToClipboard";
import { COUNTER_RESPONSE_TEMPLATE, JURISDICTIONS } from "@/lib/constants";
import { useSelection } from "@/lib/stores/selection";
import { cn } from "@/lib/utils";
import { ProcessedChannel } from "@/types";
import {
  AlertTriangle,
  ArrowLeft,
  ArrowRight,
  Check,
  CheckCircle,
  Copy,
  Download,
  ExternalLink,
  FileEdit,
  FileJson,
  Info,
  ShieldAlert,
} from "lucide-react";
import Papa from "papaparse";
import React, { useEffect, useMemo, useRef, useState } from "react";

type Flow = "deletion" | "download" | "json";
type Platform = "windows" | "macos" | "linux";

interface ExportModalProps {
  open: boolean;
  onClose: () => void;
  channels: ProcessedChannel[];
}

const PLATFORM_LABELS: Record<Platform, string> = {
  windows: "Windows",
  macos: "macOS",
  linux: "Linux",
};

const StepIndicator = ({
  currentStep,
  totalSteps,
}: {
  currentStep: number;
  totalSteps: number;
}) => (
  <div className="mb-6 flex w-full items-center justify-center px-4">
    <div className="flex w-full items-center">
      {Array.from({ length: totalSteps }).map((_, index) => (
        <React.Fragment key={index}>
          <div
            className={cn(
              "flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm font-medium",
              index < currentStep
                ? "bg-primary text-primary-foreground"
                : index === currentStep
                  ? "border-primary bg-background text-primary border-2"
                  : "bg-muted text-muted-foreground",
            )}
          >
            {index + 1}
          </div>

          {index < totalSteps - 1 && (
            <div className="flex-1">
              <div
                className={cn(
                  "h-[2px] w-full",
                  index < currentStep ? "bg-primary" : "bg-muted",
                )}
              />
            </div>
          )}
        </React.Fragment>
      ))}
    </div>
  </div>
);

const FlowSelector = ({
  onSelectFlow,
}: {
  onSelectFlow: (flow: Flow) => void;
}) => (
  <div className="space-y-8">
    <div className="text-center">
      <h2 className="text-2xl font-semibold tracking-tight">
        Choose Your Action
      </h2>
      <p className="text-muted-foreground mt-2 text-sm">
        Select how you'd like to proceed with your exported data
      </p>
    </div>

    <div className="grid grid-cols-1 gap-6 sm:grid-cols-3">
      {[
        {
          id: "deletion" as const,
          title: "Request Data Deletion",
          description:
            "Generate and submit a formal request to delete your data from Discord (servers/groups only)",
          icon: FileEdit,
        },
        {
          id: "download" as const,
          title: "Download Attachments",
          description:
            "Save all your attachments locally using our automated tools",
          icon: Download,
        },
        {
          id: "json" as const,
          title: "Export for DiscorchDeleter",
          description:
            "Export messages in JSON format for use with DiscorchDeleter (works for all message types)",
          icon: FileJson,
        },
      ].map((option) => (
        <button
          key={option.id}
          onClick={() => onSelectFlow(option.id)}
          className={cn(
            "group border-border bg-card overflow-hidden rounded-lg border text-left transition-all",
            "hover:border-primary hover:shadow-md",
            "focus-visible:ring-primary focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-hidden",
          )}
        >
          <div className="p-6">
            <div className="flex flex-col items-center text-center">
              <div className="bg-primary/10 mb-4 rounded-full p-3">
                <option.icon className="text-primary h-6 w-6" />
              </div>
              <h3 className="text-lg font-medium tracking-tight">
                {option.title}
              </h3>
              <p className="text-muted-foreground mt-2 text-sm">
                {option.description}
              </p>
              <div
                className={cn(
                  "bg-primary mt-4 inline-flex items-center justify-center rounded-full px-3 py-1",
                  "text-primary-foreground text-xs font-medium",
                  "opacity-0 transition-opacity group-hover:opacity-100",
                )}
              >
                Get Started →
              </div>
            </div>
          </div>
        </button>
      ))}
    </div>
  </div>
);

const JSONExportOptions = ({
  onExportJSON,
  includeMessageContent,
  setIncludeMessageContent,
}: {
  onExportJSON: () => void;
  includeMessageContent: boolean;
  setIncludeMessageContent: (value: boolean) => void;
}) => (
  <div className="mx-auto max-w-md space-y-6">
    <div className="space-y-2 text-center">
      <h3 className="text-xl font-semibold tracking-tight">
        Export Messages as JSON
      </h3>
      <p className="text-muted-foreground text-sm">
        Export selected messages as JSON for backup or deletion with
        DiscorchDeleter
      </p>
    </div>

    <div className="space-y-4">
      <div className="bg-muted/30 flex items-center space-x-2 rounded-md p-3">
        <Checkbox
          id="includeContent"
          checked={includeMessageContent}
          onCheckedChange={(checked) =>
            setIncludeMessageContent(checked as boolean)
          }
        />
        <div className="space-y-1">
          <label
            htmlFor="includeContent"
            className="text-sm leading-none font-medium peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
          >
            Include full message content and attachments
          </label>
          <p className="text-muted-foreground text-xs">
            Export complete message text and attachment URLs (larger file size)
          </p>
        </div>
      </div>

      <div className="border-muted bg-muted/10 flex items-start gap-3 rounded-md border p-3">
        <Info className="text-muted-foreground mt-0.5 h-5 w-5 flex-shrink-0" />
        <div className="text-muted-foreground text-sm">
          {includeMessageContent ? (
            <>
              This will create a complete backup JSON with message content and
              attachment links. Not compatible with DiscorchDeleter but useful
              for archiving.
            </>
          ) : (
            <>
              This will create a compact JSON file for use with DiscorchDeleter.
              Only includes message IDs, not full message content.
            </>
          )}
        </div>
      </div>

      <Button
        onClick={onExportJSON}
        className="flex w-full items-center justify-center gap-2"
        size="lg"
      >
        <FileJson className="h-4 w-4" />
        {includeMessageContent
          ? "Download Full JSON Backup"
          : "Download JSON for DiscorchDeleter"}
      </Button>
    </div>

    <div className="space-y-4">
      <div className="rounded-md border border-amber-200 bg-amber-50 p-4 dark:border-amber-900/50 dark:bg-amber-950/30">
        <div className="flex items-start gap-3">
          <ShieldAlert className="mt-1 h-5 w-5 flex-shrink-0 text-amber-600 dark:text-amber-400" />
          <div>
            <h4 className="text-base font-medium text-amber-800 dark:text-amber-300">
              About DiscorchDeleter
            </h4>
            <p className="mt-1 text-sm text-amber-700 dark:text-amber-400">
              <a
                href="https://github.com/Darker-Ink/DiscorchDeleter/releases"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:underline"
              >
                DiscorchDeleter
              </a>{" "}
              can delete <strong>all message types</strong> including 1:1 DMs
              that Discord refuses to delete via privacy requests. It works by
              automating the deletion process through Discord's interface.
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
);

const OpenPrivacyForm = () => (
  <div className="mx-auto max-w-md space-y-6 text-center">
    <div className="space-y-2">
      <h3 className="text-xl font-semibold tracking-tight">
        Open Discord Privacy Form
      </h3>
      <p className="text-muted-foreground text-sm">
        Start by opening Discord's official privacy request form. This will open
        in a new tab.
      </p>
    </div>

    <Button asChild size="lg" className="w-full">
      <a
        href="https://support.discord.com/hc/en-us/requests/new?ticket_form_id=4750383925911"
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center justify-center gap-2"
      >
        Open Privacy Form
        <ExternalLink className="h-4 w-4" />
      </a>
    </Button>

    <div className="rounded-lg border p-4">
      <p className="text-muted-foreground text-sm">
        After opening the form, return to this window to continue with the next
        steps of your request.
      </p>
    </div>
  </div>
);

const GenerateTemplate = () => {
  const [jurisdiction, setJurisdiction] = useState("other");
  const template = useMemo(
    () => generateTemplate(jurisdiction),
    [jurisdiction],
  );

  return (
    <div className="mx-auto max-w-md space-y-6">
      <div className="space-y-2 text-center">
        <h3 className="text-xl font-semibold tracking-tight">
          Generate Request Template
        </h3>
        <p className="text-muted-foreground text-sm">
          Copy the template below to use in your deletion request
        </p>
      </div>

      <div className="space-y-4">
        <div className="space-y-2">
          <label className="text-sm font-medium">
            Select Your Jurisdiction
          </label>
          <Select value={jurisdiction} onValueChange={setJurisdiction}>
            <SelectTrigger>
              <SelectValue placeholder="Choose your jurisdiction" />
            </SelectTrigger>
            <SelectContent>
              <SelectGroup>
                <SelectLabel>Legal Framework</SelectLabel>
                {Object.entries(JURISDICTIONS).map(([key, j]) => (
                  <SelectItem key={key} value={j.value}>
                    {j.label}
                  </SelectItem>
                ))}
              </SelectGroup>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <label className="text-sm font-medium">Request Template</label>
          <Textarea
            value={template}
            readOnly
            className="h-[200px] resize-none font-mono text-sm"
          />
        </div>

        <CopyButton text={template} className="w-full" size="lg" />
      </div>
    </div>
  );
};

const FillFormInstructions = () => (
  <div className="mx-auto max-w-md space-y-6">
    <div className="space-y-2 text-center">
      <h3 className="text-xl font-semibold tracking-tight">
        Fill Out the Privacy Form
      </h3>
      <p className="text-muted-foreground text-sm">
        Complete each field in the Discord privacy form
      </p>
    </div>

    <div className="bg-muted/50 space-y-4 rounded-lg border p-4">
      {[
        {
          title: "Email Address",
          description: "Enter your Discord account email address",
        },
        {
          title: "Subject",
          description: "Enter a subject for your request",
        },
        {
          title: "Description",
          description: "Paste the template you generated in the previous step",
        },
        {
          title: "Request Type",
          description: 'Select "Delete your personal data on Discord"',
        },
        {
          title: "Confirmation",
          description: 'Check the "Have you read ...?" box',
        },
      ].map((step, index) => (
        <div key={index} className="flex gap-3">
          <div className="bg-primary/10 text-primary flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-sm font-medium">
            {index + 1}
          </div>
          <div className="space-y-0.5">
            <h4 className="text-sm font-medium">{step.title}</h4>
            <p className="text-muted-foreground text-sm">{step.description}</p>
          </div>
        </div>
      ))}
    </div>

    <div className="flex items-start gap-3 rounded-lg border-2 border-red-500/20 bg-red-500/10 p-4">
      <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-red-500" />
      <div className="space-y-2 text-sm">
        <span className="font-medium text-red-500">Important Limitations:</span>
        <ul className="text-muted-foreground ml-4 list-disc space-y-1">
          <li>
            <strong>Leave servers and group DMs first</strong> - Discord won't
            delete messages from channels you're still in
          </li>
          <li>
            <strong>1:1 DMs won't be deleted</strong> - Discord refuses these
            requests entirely
          </li>
          <li>
            Only server messages and left group DM messages will be processed
          </li>
        </ul>
      </div>
    </div>

    <div className="flex items-start gap-3 rounded-lg border-2 border-amber-500/20 bg-amber-500/10 p-4">
      <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-500" />
      <div className="text-sm">
        <span className="font-medium text-amber-500">Important: </span>
        Don't forget to attach your message data CSV file, which you'll download
        in the next step
      </div>
    </div>
  </div>
);

const DownloadCSV = ({
  selectedData,
}: {
  selectedData: ProcessedChannel[];
}) => {
  const handleDownload = () => {
    const data = formatDataForCSV(selectedData);
    downloadFile(
      Papa.unparse(data),
      "discord_deletion_request.csv",
      "text/csv",
    );
  };

  return (
    <div className="mx-auto max-w-md space-y-6">
      <div className="space-y-2 text-center">
        <h3 className="text-xl font-semibold tracking-tight">
          Download Your Data
        </h3>
        <p className="text-muted-foreground text-sm">
          Download your message data as a CSV file to attach to your request
        </p>
      </div>

      <div className="text-center">
        <Button size="lg" onClick={handleDownload} className="w-full">
          <Download className="mr-2 h-4 w-4" />
          Download Message Data
        </Button>
      </div>

      <div className="rounded-lg border p-4">
        <div className="flex gap-2">
          <Info className="text-muted-foreground mt-0.5 h-4 w-4 shrink-0" />
          <p className="text-muted-foreground text-sm">
            This CSV file contains the message IDs needed for Discord to process
            your deletion request. You'll need to attach this file in the
            privacy form.
          </p>
        </div>
      </div>
    </div>
  );
};

const ReviewAndSubmit = () => (
  <div className="mx-auto max-w-md space-y-6">
    <div className="space-y-2 text-center">
      <h3 className="text-xl font-semibold tracking-tight">
        Review and Submit
      </h3>
      <p className="text-muted-foreground text-sm">
        Final steps to complete your deletion request
      </p>
    </div>

    <div className="space-y-3">
      {[
        "Verify all form fields are filled out correctly",
        "Confirm the CSV file is attached to your request",
        "Double-check your email address is correct",
      ].map((item, index) => (
        <div
          key={index}
          className="bg-muted/50 flex items-start gap-3 rounded-lg border p-3"
        >
          <Check className="text-primary mt-0.5 h-4 w-4 shrink-0" />
          <span className="text-sm">{item}</span>
        </div>
      ))}
    </div>

    <div className="rounded-lg border p-4">
      <div className="flex gap-2">
        <Info className="text-muted-foreground mt-0.5 h-4 w-4 shrink-0" />
        <p className="text-muted-foreground text-sm">
          After submitting your request, you'll receive updates about its status
          via email from Discord's privacy team.
        </p>
      </div>
    </div>
  </div>
);

interface FileTypeStats {
  avgSize: number;
  extensions: string[];
}

const FILE_TYPE_STATS: Record<string, FileTypeStats> = {
  "video/mp4": {
    avgSize: (30.6 * 1024 * 1024 * 1024) / 1000,
    extensions: [".mp4"],
  },
  "image/png": {
    avgSize: 398.7 * 1024,
    extensions: [".png"],
  },
  "image/jpeg": {
    avgSize: 893.8 * 1024,
    extensions: [".jpg", ".jpeg"],
  },
  "application/octet-stream": {
    avgSize: 34.9 * 1024 * 1024,
    extensions: [".mov", ".mp3", ".mp4", ".png", ".tif"],
  },
  "video/webm": {
    avgSize: 30.1 * 1024 * 1024,
    extensions: [".webm", ".mkv"],
  },
  default: {
    avgSize: 2 * 1024 * 1024,
    extensions: [],
  },
};

function formatSize(bytes: number): string {
  const units = ["B", "KB", "MB", "GB", "TB"];
  let size = bytes;
  let unitIndex = 0;

  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024;
    unitIndex++;
  }

  const rounded = Math.round(size * 10) / 10;
  return `${rounded} ${units[unitIndex]}`;
}

function estimateTotalSize(urls: string[]): {
  bytes: number;
  formatted: string;
} {
  let totalBytes = 0;

  for (const url of urls) {
    const extension = url.split(".").pop()?.toLowerCase();
    if (!extension) {
      totalBytes += FILE_TYPE_STATS.default.avgSize;
      continue;
    }

    const matchingType = Object.entries(FILE_TYPE_STATS).find(([, stats]) =>
      stats.extensions.includes(`.${extension}`),
    );

    if (matchingType) {
      totalBytes += matchingType[1].avgSize;
    } else {
      totalBytes += FILE_TYPE_STATS.default.avgSize;
    }
  }

  return {
    bytes: totalBytes,
    formatted: formatSize(totalBytes),
  };
}

function* urlGenerator(channels: ProcessedChannel[]): Generator<string> {
  const selectedMessages = useSelection.getState().selectedMessages;

  for (const channel of channels) {
    const selectedMessageIds = selectedMessages.get(channel.id);
    if (!selectedMessageIds) continue;

    for (const message of channel.messages) {
      if (
        selectedMessageIds.has(message.ID) &&
        message.parsedAttachments?.length
      ) {
        for (const attachment of message.parsedAttachments) {
          if (attachment.url) {
            yield attachment.url;
          }
        }
      }
    }
  }
}

function detectOS(): Platform {
  if (typeof window === "undefined") return "windows";

  const { userAgent } = window.navigator;
  if (/windows/i.test(userAgent)) return "windows";
  if (/macintosh|mac os x/i.test(userAgent)) return "macos";
  if (/linux/i.test(userAgent)) return "linux";
  return "windows";
}

function RaphaelWindows(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="1em"
      height="1em"
      viewBox="0 0 32 32"
      {...props}
    >
      <path
        fill="currentColor"
        d="M20.023 17.484c-1.732-.205-3.022-.908-4.212-1.7l-.558.278l-2.578 8.924c1.217.805 2.905 1.707 4.682 1.914c2.686.312 5.56-.744 6.39-1.195l2.618-9.06l-.56-.28s-2.61 1.492-5.78 1.12zm-5.6-2.66c-1.266-.87-2.577-1.65-4.374-1.815a10 10 0 0 0-.926-.043c-3.01 0-4.948 1.347-4.948 1.347L1.61 23.19l.527.282a10.1 10.1 0 0 1 5.09-.984c1.665.113 2.92.78 4.117 1.53l.507-.26l2.574-8.933zm-4.222-2.73c1.665.114 2.922.78 4.118 1.533l.51-.26L17.4 4.43c-1.27-.87-2.58-1.652-4.377-1.815a10 10 0 0 0-.924-.042c-3.012 0-4.95 1.347-4.95 1.347l-2.566 8.878l.526.282a10.1 10.1 0 0 1 5.09-.986zM28.78 5.97c0 .002-2.61 1.493-5.78 1.12c-1.734-.204-3.023-.907-4.213-1.7l-.56.28l-2.576 8.923c1.216.803 2.907 1.71 4.68 1.915c2.688.312 5.56-.745 6.393-1.197l2.615-9.058l-.56-.28z"
      ></path>
    </svg>
  );
}

function RaphaelApple(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="1em"
      height="1em"
      viewBox="0 0 32 32"
      {...props}
    >
      <path
        fill="currentColor"
        d="M24.32 10.85q-2.615 1.851-2.615 4.455q0 3.118 3.232 4.786q-.867 2.516-2.514 4.376q-1.645 1.861-3.004 1.862q-.641-.002-1.75-.423l-.353-.138q-1.086-.421-1.92-.423q-.787 0-1.725.33l-.445.16l-.56.23q-.662.264-1.337.264q-1.59 0-3.507-2.616q-2.764-3.747-2.764-8.167q-.001-3.141 1.724-5.06q1.725-1.92 4.568-1.92c.71 0 1.37.13 1.988.388l.423.172l.445.183q.594.25.96.25q.466.002 1.04-.216l.58-.23l.436-.16q1.04-.376 2.297-.376q2.987 0 4.8 2.274zm-4.705-7.563q.032.4.033.617q0 1.976-1.438 3.467c-1.438 1.491-2.075 1.49-3.347 1.49a6 6 0 0 1-.058-.638q0-1.68 1.337-3.153q1.335-1.476 3.096-1.726q.125-.022.377-.057"
      ></path>
    </svg>
  );
}

function RaphaelLinux(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="1em"
      height="1em"
      viewBox="0 0 32 32"
      {...props}
    >
      <path
        fill="currentColor"
        d="M11.79 25.23a.988.988 0 0 0 .68-1.667c-.397-.392-2.597-2.022-3.17-2.664c-.267-.3-.627-.446-.78-.78c-.352-.77-.598-1.87-.15-2.658c.08-.144.132-.078.07.22c-.35 1.684.746 3.06.986 2.354c.167-.487.013-1.358.102-2.05c.158-1.227 1.273-3.578 1.763-3.713c-.754-1.398.887-2.494.867-3.723c-.014-.798.7.982 1.42 1.36c.8.42 1.683-.795 2.935-1.41c.354-.177.81-.377.776-.525c-.146-.718-1.644.886-2.98.94c-.61.023-.836-.12-1.07-.348c-.713-.69.072-.115 1.13-.307c.472-.085.63-.163 1.13-.365c.5-.2 1.068-.5 1.635-.654c.395-.105.36-.4.208-.49c-.087-.05-.218-.046-.32.133c-.244.42-1.383.66-1.74.77c-.457.14-.962.272-1.634.244c-1.02-.042-.782-.51-1.513-.928c-.213-.123-.156-.445.13-.73c.147-.148.556-.232.76-.572c.027-.047.288-.32.493-.46c.07-.05.076-1.296-.562-1.32c-.543-.022-.697.397-.675.817c.02.42.244.765.392.764c.285-.005.02.31-.138.36c-.237.078-.562-.934-.525-1.418c.038-.506.302-1.4.94-1.383c.577.015.994.736.974 1.982c-.003.21.935-.1 1.247.23c.225.235-.766-2.208 1.44-2.376c.58.11 1.14.305 1.37 1.64c-.086.14.146 1.07-.215 1.183c-.438.134-.707-.02-.453-.44c.172-.417.004-1.482-.882-1.42c-.887.065-.77 1.638-.526 1.67c.243.03.854.464 1.282.548c1.4.27.37 1.075.555 2.048c.206 1.1.93.81 1.58 3.717c.136.177.675.345 1.198 2.58c.473 2.01-.195 3.472.938 3.352c.256-.026.63-.1.792-.668c.425-1.49-.213-3.263-.855-4.46c-.375-.698-.73-1.174-.916-1.337c.738.436 1.683 1.83 1.898 2.862c.286 1.358.49 1.934.06 3.37c.25.125.87.39.87.685c-.647-.53-2.63-.625-2.68.646c-.338.008-.594.034-.81.293c-.798.944-.06 2.842-.14 3.86c-.07.895-.318 1.782-.46 2.682c-.474-.02-.428-.364-.274-.852c.136-.43.352-.968.366-1.484c.012-.467-.04-.76-.156-.83c-.118-.073-.303.073-.56.484c-.542.875-1.72 1.26-2.82 1.397c-1.1.138-2.123.028-2.664-.578c-.186-.207-.492.058-.53.11c-.048.075.18.22.353.534c.25.46.49 1.158-.106 1.478c.006-1.624-.508-1.717-1.033-2.71zm-.392-.042c.395.62 1.783 3.232-.652 3.57c-.814.115-2.125-.473-3.396-.783c-1.142-.28-2.3-.444-2.95-.627c-.39-.108-.553-.25-.587-.414c-.09-.434.474-1.04.503-1.555c.028-.515-.188-.78-.364-1.2c-.177-.42-.224-.734-.08-.914c.108-.14.333-.2.697-.164c.46.047 1.02-.05 1.318-.23c.505-.31.742-.94.516-1.7c0 .745-.244 1.026-.855 1.367c-.578.318-1.468.06-1.876.415c-.492.427.175 1.528.12 2.338c-.042.622-.69 1.322-.4 1.946c.29.626 1.647.694 3.063.99c2.012.42 3.184 1.152 4.113 1.187c1.356.05 1.564-1.342 3.693-1.36a46 46 0 0 1 1.835-.06c.688-.01 1.375-.003 2.08.014c1.416.035.93.774 1.85 1.247c.774.397 2.17.24 2.504-.077c.45-.43 1.662-1.467 2.592-1.935c1.156-.583 3.876-1.588 1.902-2.812c-.46-.285-1.547-.588-1.64-2.676c-.41.366-.364 2.312.785 2.697c1.284.43 2.086 1.152-.3 1.97c-1.58.54-1.85.705-3.1 1.746c-1.266 1.054-3.144.636-2.814-1.582c.17-1.155.27-2.11-.02-3.114c-.14-.49-.21-1.12-.113-1.562c.187-.858.65-1.117 1.106-.293c.284.518.384 1.12 1.407 1.17c1.607.077 1.926-1.553 2.44-1.627c.342-.05.685-1.02.424-2.59c-.28-1.68-1.268-4.33-2.535-5.676C21.612 11.78 20.948 10.8 20.53 9.4c-.352-1.175-.547-2.318-.475-3.412c.094-1.417-.69-3.39-1.943-4.316c-.782-.58-2.01-.893-3.122-.88c-.623.007-1.21.1-1.66.343c-1.856 1.008-2.114 2.445-2.087 4.088c.025 1.543.078 3.303.254 4.977c-.208.77-1.288 2.227-1.98 3.114c-.927.92-1.396 2.696-1.997 4.247c-.32.83-.862 1.2-.908 2.266c-.012.296-.002 1.065.282.846c1.086-.843 2.45 1.278 4.504 4.516zm5.646-22.235c-.06.176-.3.32-.146.443c.152.123.24-.17.55-.28c.08-.03.448.01.518-.165c.03-.076-.19-.163-.32-.29c-.134-.125-.263-.236-.387-.23c-.322.02-.164.368-.216.523zm1.89 6.397c.115-.12.174.207.483.402c.244.154.48.04.545.354c.044.225-.097.467-.284.436c-.328-.056-1.082-.837-.744-1.192m-5.102-1.975c-.508-.037-.543.33-.375.324c.172-.007.066-.292.375-.325zm-.872-.94c.06-.012.146.09.12.234c-.038.198-.022.323.116.324c.022 0 .048-.005.056-.057c.066-.396-.14-.688-.225-.71c-.193-.05-.17.23-.067.21zm3.703-.167c.13.04.253.262.28.504c.002.02.168-.035.17-.088c.01-.39-.32-.57-.408-.562c-.2.017-.143.116-.042.146m-1.898 1.155c.463-.214.625.118.465.17c-.164.055-.165-.248-.465-.17m-5.587 7.88c-.22-.025.063-.19.184-.396c.13-.227.105-.51.244-.47s.06.2-.033.462c-.082.22-.315.413-.395.404"
      ></path>
    </svg>
  );
}

const CommandDisplay = ({
  label,
  commands,
}: {
  label?: string;
  commands: string[];
}) => {
  const { copied, copy } = useCopyToClipboard();
  const allCommands = commands.join("\n");

  return (
    <div className="space-y-1.5">
      {label && (
        <div className="text-muted-foreground text-xs font-medium">{label}</div>
      )}
      <div className="group relative overflow-hidden rounded-md border bg-zinc-950 shadow-xs">
        <button
          onClick={() => copy(allCommands)}
          className={cn(
            "absolute top-2 right-2 rounded-md border border-zinc-800 p-1.5",
            "bg-zinc-900/80 backdrop-blur-xs",
            "opacity-0 transition-opacity group-hover:opacity-100",
            "text-zinc-400 hover:text-zinc-200",
            copied && "text-emerald-400 hover:text-emerald-300",
          )}
          title={copied ? "Copied!" : "Copy to clipboard"}
        >
          {copied ? (
            <Check className="h-3.5 w-3.5" />
          ) : (
            <Copy className="h-3.5 w-3.5" />
          )}
        </button>

        <div className="overflow-x-auto p-3 font-mono text-xs leading-relaxed text-zinc-100">
          {commands.map((cmd, i) => (
            <div
              key={i}
              className="selection:bg-zinc-800"
              style={{ userSelect: "text" }}
            >
              <span className="mr-2 text-zinc-500 select-none">$</span>
              {cmd}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

interface InstructionLink {
  text: string;
  url: string;
}

interface PlatformInstruction {
  title: string;
  description: string;
  commands?: string[];
  links?: InstructionLink[];
  note?: string;
}

interface DependencyInfo {
  name: string;
  description: string;
  instructions: PlatformInstruction[];
}

const PLATFORM_DEPENDENCIES: Record<Platform, DependencyInfo[]> = {
  windows: [
    {
      name: "Git",
      description: "Version control system needed to download the source code",
      instructions: [
        {
          title: "Download Git for Windows",
          description:
            "Install Git with Git Bash (recommended for Windows users)",
          links: [
            {
              text: "Download Git for Windows",
              url: "https://git-scm.com/download/windows",
            },
          ],
          note: "During installation, make sure to select 'Git Bash' when prompted",
        },
      ],
    },
    {
      name: "Go",
      description: "The Go programming language runtime",
      instructions: [
        {
          title: "Download Go for Windows",
          description: "Install the latest version of Go",
          links: [
            {
              text: "Download Go for Windows",
              url: "https://go.dev/dl/",
            },
          ],
          note: "After installation, you may need to restart your terminal",
        },
      ],
    },
  ],
  macos: [
    {
      name: "Git",
      description: "Version control system needed to download the source code",
      instructions: [
        {
          title: "Install via Homebrew (Recommended)",
          description: "If you have Homebrew installed, use:",
          commands: ["brew install git"],
        },
        {
          title: "Alternative: Manual Download",
          description: "If you don't have Homebrew, download the installer:",
          links: [
            {
              text: "Download Git for macOS",
              url: "https://git-scm.com/download/mac",
            },
          ],
        },
      ],
    },
    {
      name: "Go",
      description: "The Go programming language runtime",
      instructions: [
        {
          title: "Install via Homebrew (Recommended)",
          description: "If you have Homebrew installed, use:",
          commands: ["brew install go"],
        },
        {
          title: "Alternative: Manual Download",
          description: "If you don't have Homebrew, download the installer:",
          links: [
            {
              text: "Download Go for macOS",
              url: "https://go.dev/dl/",
            },
          ],
        },
      ],
    },
  ],
  linux: [
    {
      name: "Git",
      description: "Version control system needed to download the source code",
      instructions: [
        {
          title: "Ubuntu/Debian",
          description: "Install using apt:",
          commands: ["sudo apt update", "sudo apt install git"],
        },
        {
          title: "Fedora",
          description: "Install using dnf:",
          commands: ["sudo dnf install git"],
        },
        {
          title: "Arch Linux",
          description: "Install using pacman:",
          commands: ["sudo pacman -S git"],
        },
      ],
    },
    {
      name: "Go",
      description: "The Go programming language runtime",
      instructions: [
        {
          title: "Ubuntu/Debian",
          description: "Install using apt:",
          commands: ["sudo apt update", "sudo apt install golang"],
        },
        {
          title: "Fedora",
          description: "Install using dnf:",
          commands: ["sudo dnf install golang"],
        },
        {
          title: "Arch Linux",
          description: "Install using pacman:",
          commands: ["sudo pacman -S go"],
        },
      ],
    },
  ],
};

interface BuildStep {
  title: string;
  description: string;
  commands: string[];
  note?: string;
}

const BUILD_STEPS: Record<Platform, BuildStep[]> = {
  windows: [
    {
      title: "Clone Repository",
      description: "Download the source code using Git Bash",
      commands: [
        "cd %USERPROFILE%\\Downloads",
        "git clone https://codeberg.org/discorch/discorch.git",
        "cd discorch",
      ],
      note: "Make sure to use Git Bash terminal for these commands",
    },
    {
      title: "Build Downloader",
      description: "Compile the downloader program",
      commands: ["go build -o bin/downloader.exe ./cmd/downloader"],
    },
  ],
  macos: [
    {
      title: "Clone Repository",
      description: "Download the source code",
      commands: [
        "cd ~/Downloads",
        "git clone https://codeberg.org/discorch/discorch.git",
        "cd discorch",
      ],
    },
    {
      title: "Build Downloader",
      description: "Compile the downloader program",
      commands: ["go build -o bin/downloader ./cmd/downloader"],
    },
  ],
  linux: [
    {
      title: "Clone Repository",
      description: "Download the source code",
      commands: [
        "cd ~/Downloads",
        "git clone https://codeberg.org/discorch/discorch.git",
        "cd discorch",
      ],
    },
    {
      title: "Build Downloader",
      description: "Compile the downloader program",
      commands: ["go build -o bin/downloader ./cmd/downloader"],
    },
  ],
};

interface RunStep {
  title: string;
  description: string;
  commands: string[];
  note?: string;
}

const RUN_STEPS: Record<Platform, RunStep[]> = {
  windows: [
    {
      title: "Open Terminal",
      description: "Open Git Bash or Command Prompt",
      commands: [],
      note: "You can use either Git Bash (recommended) or Command Prompt",
    },
    {
      title: "Navigate to Downloads",
      description: "Go to your Downloads folder",
      commands: ["cd %USERPROFILE%\\Downloads"],
    },
    {
      title: "Run Downloader",
      description: "Start the download process",
      commands: ["discorch\\bin\\downloader.exe"],
      note: "The program will automatically find urls.txt and start downloading",
    },
  ],
  macos: [
    {
      title: "Open Terminal",
      description: "Open Terminal application",
      commands: [],
      note: "You can find Terminal in Applications → Utilities, or use Spotlight (⌘ + Space) and type 'Terminal'",
    },
    {
      title: "Navigate to Downloads",
      description: "Go to your Downloads folder",
      commands: ["cd ~/Downloads"],
    },
    {
      title: "Run Downloader",
      description: "Start the download process",
      commands: ["discorch/bin/downloader"],
      note: "The program will automatically find urls.txt and start downloading",
    },
  ],
  linux: [
    {
      title: "Open Terminal",
      description: "Open your terminal application",
      commands: [],
    },
    {
      title: "Navigate to Downloads",
      description: "Go to your Downloads folder",
      commands: ["cd ~/Downloads"],
    },
    {
      title: "Run Downloader",
      description: "Start the download process",
      commands: ["discorch/bin/downloader"],
      note: "The program will automatically find urls.txt and start downloading",
    },
  ],
};

const LinkButton = ({ text, url }: InstructionLink) => (
  <Button variant="outline" className="w-full justify-start gap-2" asChild>
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center"
    >
      <Download className="h-4 w-4" />
      {text}
      <ExternalLink className="ml-auto h-4 w-4" />
    </a>
  </Button>
);

const PlatformSelector = ({
  selectedPlatform,
  onSelectPlatform,
}: {
  selectedPlatform: Platform;
  onSelectPlatform: (platform: Platform) => void;
}) => {
  return (
    <div className="mx-auto max-w-md space-y-6">
      <div className="space-y-2 text-center">
        <h3 className="text-xl font-semibold tracking-tight">
          Select Your Platform
        </h3>
        <p className="text-muted-foreground text-sm">
          Choose your operating system to get platform-specific instructions
        </p>
      </div>

      <div className="space-y-2">
        {(Object.keys(PLATFORM_LABELS) as Platform[]).map((platform) => (
          <button
            key={platform}
            onClick={() => onSelectPlatform(platform)}
            className={cn(
              "flex w-full items-center gap-4 rounded-lg border p-4 text-left transition-colors",
              selectedPlatform === platform
                ? "border-primary bg-primary/5 text-primary"
                : "hover:bg-muted/50",
            )}
          >
            <div className="bg-background flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border">
              {platform === "windows" && <RaphaelWindows className="h-5 w-5" />}
              {platform === "macos" && <RaphaelApple className="h-5 w-5" />}
              {platform === "linux" && <RaphaelLinux className="h-5 w-5" />}
            </div>
            <div>
              <div className="font-medium">{PLATFORM_LABELS[platform]}</div>
              <div className="text-muted-foreground text-sm">
                Build and run instructions for {PLATFORM_LABELS[platform]}
              </div>
            </div>
            {selectedPlatform === platform && (
              <Check className="ml-auto h-4 w-4 shrink-0" />
            )}
          </button>
        ))}
      </div>
    </div>
  );
};

const DependencyInstructions = ({ platform }: { platform: Platform }) => {
  return (
    <div className="space-y-6">
      {PLATFORM_DEPENDENCIES[platform].map((dep) => (
        <div key={dep.name} className="bg-card rounded-lg border p-4">
          <div className="mb-4">
            <h4 className="font-medium">{dep.name}</h4>
            <p className="text-muted-foreground text-sm">{dep.description}</p>
          </div>

          <div className="space-y-4">
            {dep.instructions.map((inst, index) => (
              <div key={index} className="space-y-3">
                <div>
                  <h5 className="font-medium">{inst.title}</h5>
                  <p className="text-muted-foreground text-sm">
                    {inst.description}
                  </p>
                </div>

                {inst.commands && <CommandDisplay commands={inst.commands} />}

                {inst.links?.map((link, i) => <LinkButton key={i} {...link} />)}

                {inst.note && (
                  <div className="bg-muted text-muted-foreground flex items-start gap-2 rounded-md p-3 text-sm">
                    <Info className="mt-0.5 h-4 w-4 shrink-0" />
                    {inst.note}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

const BuildInstructions = ({ platform }: { platform: Platform }) => {
  return (
    <div className="space-y-6">
      <div className="border-primary/20 bg-primary/5 rounded-lg border-2 p-4">
        <div className="flex gap-2">
          <Info className="text-primary mt-0.5 h-5 w-5 shrink-0" />
          <div className="space-y-2 text-sm">
            <p className="text-primary font-medium">Build Process</p>
            <p className="text-muted-foreground">
              Follow these steps in order. Each step builds upon the previous
              one. Make sure each command completes successfully before moving
              to the next.
            </p>
          </div>
        </div>
      </div>

      <div className="space-y-4">
        {BUILD_STEPS[platform].map((step, index) => (
          <div key={index} className="bg-card rounded-lg border p-4">
            <div className="mb-3 flex items-center gap-3">
              <div className="bg-primary/10 text-primary flex h-6 w-6 items-center justify-center rounded-full text-sm font-medium">
                {index + 1}
              </div>
              <div>
                <div className="font-medium">{step.title}</div>
                <div className="text-muted-foreground text-sm">
                  {step.description}
                </div>
              </div>
            </div>

            <div className="space-y-3">
              <CommandDisplay commands={step.commands} />
              {step.note && (
                <div className="bg-muted text-muted-foreground flex items-start gap-2 rounded-md p-3 text-sm">
                  <Info className="mt-0.5 h-4 w-4 shrink-0" />
                  {step.note}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const RunInstructions = ({ platform }: { platform: Platform }) => {
  return (
    <div className="space-y-6">
      <div className="space-y-4">
        {RUN_STEPS[platform].map((step, index) => (
          <div key={index} className="bg-card rounded-lg border p-4">
            <div className="mb-3 flex items-center gap-3">
              <div className="bg-primary/10 text-primary flex h-6 w-6 items-center justify-center rounded-full text-sm font-medium">
                {index + 1}
              </div>
              <div>
                <div className="font-medium">{step.title}</div>
                <div className="text-muted-foreground text-sm">
                  {step.description}
                </div>
              </div>
            </div>

            {step.commands.length > 0 && (
              <div className="space-y-3">
                <CommandDisplay commands={step.commands} />
              </div>
            )}

            {step.note && (
              <div className="bg-muted text-muted-foreground mt-3 flex items-start gap-2 rounded-md p-3 text-sm">
                <Info className="mt-0.5 h-4 w-4 shrink-0" />
                {step.note}
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="flex items-start gap-3 rounded-lg border-2 border-amber-500/20 bg-amber-500/10 p-4">
        <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-500" />
        <div className="space-y-2 text-sm">
          <p className="font-medium text-amber-500">Important Notes:</p>
          <ul className="text-muted-foreground list-inside list-disc space-y-1">
            <li>
              The download process may take a while depending on the number and
              size of attachments
            </li>
            <li>
              You can safely interrupt and resume the download at any time
            </li>
            <li>Files will be organized in a new 'downloads' folder</li>
            <li>
              The program will skip files that have already been downloaded
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

const DownloadURLList = ({
  selectedData,
}: {
  selectedData: ProcessedChannel[];
}) => {
  const urls = useMemo(
    () => Array.from(urlGenerator(selectedData)),
    [selectedData],
  );
  const estimate = useMemo(() => estimateTotalSize(urls), [urls]);
  const fileCount = urls.length.toLocaleString();

  const handleDownload = () => {
    if (urls.length === 0) {
      alert("No attachment URLs found in your selected messages.");
      return;
    }
    downloadFile(urls.join("\n"), "urls.txt", "text/plain");
  };

  return (
    <div className="mx-auto max-w-md space-y-6">
      <div className="text-center">
        <h3 className="text-xl font-medium tracking-tight">
          Download URL List
        </h3>
        <p className="text-muted-foreground mt-1.5 text-sm">
          {urls.length === 0
            ? "No attachment URLs found"
            : `Save a list of ${fileCount} attachment URLs`}
        </p>
      </div>

      <Button
        size="lg"
        onClick={handleDownload}
        className="w-full"
        disabled={urls.length === 0}
      >
        <Download className="mr-2 h-4 w-4" />
        {urls.length === 0 ? "No URLs to Download" : "Download URLs"}
      </Button>

      {urls.length > 0 && (
        <div className="bg-card rounded-lg border">
          <div className="flex flex-col items-center p-6 text-center">
            <div className="text-muted-foreground mb-1 text-sm font-medium">
              Estimated Storage Required
            </div>
            <div className="text-primary text-3xl font-semibold tracking-tight">
              {estimate.formatted}
            </div>
            <div className="text-muted-foreground mt-1.5 text-xs">
              This is an estimate based on average file sizes.
              <br />
              The actual size may vary.
            </div>
          </div>

          <div className="bg-muted/50 border-t px-6 py-4 text-center">
            <div className="text-muted-foreground space-y-1.5 text-xs">
              <p>
                After downloading, consider sharing your "File Statistics by
                Type" to help us improve these estimates.
              </p>
              <Button
                variant="link"
                className="h-auto p-0 text-xs font-normal"
                asChild
              >
                <a
                  href="https://codeberg.org/discorch/discorch/issues/new"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-muted-foreground hover:text-primary inline-flex items-center gap-1"
                >
                  Share your download stats
                  <ExternalLink className="-ml-[.25em] h-3 w-3" />
                </a>
              </Button>
            </div>
          </div>
        </div>
      )}

      {urls.length === 0 && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 dark:border-amber-900/50 dark:bg-amber-950/30">
          <div className="flex gap-2">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400" />
            <div className="text-sm">
              <p className="font-medium text-amber-800 dark:text-amber-300">
                No Attachments Found
              </p>
              <p className="mt-1 text-amber-700 dark:text-amber-400">
                Your selected messages don't contain any downloadable
                attachments. Make sure you've selected messages that include
                images, videos, or other file attachments.
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="rounded-lg border p-4">
        <div className="flex gap-2">
          <Info className="text-muted-foreground mt-0.5 h-4 w-4 shrink-0" />
          <p className="text-muted-foreground text-sm">
            This file contains direct links to all your attachments. You'll use
            this list with the downloader program in the next steps.
          </p>
        </div>
      </div>
    </div>
  );
};

const CompletionStep = ({
  flow,
  onClose,
  onFlowSelect,
}: {
  flow: Flow;
  onClose: () => void;
  onFlowSelect: (flow: Flow) => void;
}) => {
  return (
    <div className="mx-auto max-w-md space-y-6">
      <div className="text-center">
        <div className="mb-3 flex justify-center">
          <div className="rounded-full bg-green-500/10 p-3">
            <CheckCircle className="h-8 w-8 text-green-500" />
          </div>
        </div>
        <h3 className="text-xl font-semibold tracking-tight">
          {flow === "deletion"
            ? "Deletion Request Prepared"
            : flow === "download"
              ? "Attachment Downloads Ready"
              : "JSON Export Complete"}
        </h3>
        <p className="text-muted-foreground mt-2 text-sm">
          {flow === "deletion"
            ? "Your deletion request is ready to be submitted to Discord's privacy team"
            : flow === "download"
              ? "You're all set to download your attachments using the downloader"
              : "Your JSON file has been downloaded and is ready for use with DiscorchDeleter"}
        </p>
      </div>

      <div className="grid gap-4">
        {flow !== "json" && (
          <Card
            className={cn(
              "relative cursor-pointer overflow-hidden border-2 transition-colors",
              "bg-card hover:bg-accent",
              "border-border hover:border-primary",
            )}
            onClick={() => onFlowSelect("json")}
          >
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <FileJson className="h-5 w-5" />
                Export for DiscorchDeleter
              </CardTitle>
              <CardDescription>
                Export messages for deletion with DiscorchDeleter (works for all
                message types including 1:1 DMs)
              </CardDescription>
            </CardHeader>
          </Card>
        )}

        {flow === "deletion" && (
          <>
            <div className="rounded-lg border border-red-500/20 bg-red-500/10 p-4">
              <div className="flex gap-3">
                <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-red-500" />
                <div className="space-y-2 text-sm">
                  <p className="font-medium text-red-500">
                    Discord's Limitations
                  </p>
                  <p className="text-muted-foreground">
                    Remember: Discord will NOT delete 1:1 DMs via privacy
                    requests. For those, you need DiscorchDeleter.
                  </p>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onFlowSelect("json")}
                    className="w-full"
                  >
                    <FileJson className="mr-2 h-4 w-4" />
                    Export for DiscorchDeleter Instead
                  </Button>
                </div>
              </div>
            </div>

            <div className="border-muted bg-muted/30 rounded-lg border p-4">
              <div className="flex gap-3">
                <AlertTriangle className="text-muted-foreground mt-0.5 h-5 w-5 shrink-0" />
                <div className="space-y-2 text-sm">
                  <p className="font-medium">Discord's Reply Process</p>
                  <p className="text-muted-foreground">
                    Discord will likely reply with "This channel is reserved for
                    individuals..." You'll need to use the template below to get
                    your request reviewed properly.
                  </p>
                  <div className="space-y-2">
                    <p className="text-foreground font-medium">
                      Response template:
                    </p>
                    <div className="bg-muted/50 rounded border p-3">
                      <pre className="text-xs whitespace-pre-wrap">
                        {COUNTER_RESPONSE_TEMPLATE}
                      </pre>
                    </div>
                    <CopyButton
                      text={COUNTER_RESPONSE_TEMPLATE}
                      variant="outline"
                      size="sm"
                      className="w-full"
                    />
                  </div>
                </div>
              </div>
            </div>
          </>
        )}

        <Button variant="outline" onClick={onClose} className="w-full">
          Close
        </Button>
      </div>
    </div>
  );
};

function getSelectionStats(): {
  messages: number;
  channels: number;
} {
  const selectedMessages = useSelection.getState().selectedMessages;

  let messageCount = 0;
  let channelCount = 0;

  for (const [, messageIds] of selectedMessages.entries()) {
    if (messageIds.size > 0) {
      channelCount++;
      messageCount += messageIds.size;
    }
  }

  return {
    messages: messageCount,
    channels: channelCount,
  };
}

function generateTemplate(jurisdiction: string): string {
  const selectionStats = getSelectionStats();

  const baseTemplate = `Hello,

${JURISDICTIONS[jurisdiction as keyof typeof JURISDICTIONS]?.text || "I am writing to request the deletion of my personal data from Discord."}

I understand that Discord will only delete messages from servers and group DMs that I have already left. I have left all relevant servers and group DMs containing the messages I wish to delete.

Attached to this request is a CSV file containing ${selectionStats.messages.toLocaleString()} ${
    selectionStats.messages === 1 ? "message" : "messages"
  } across ${selectionStats.channels.toLocaleString()} ${
    selectionStats.channels === 1 ? "channel" : "channels"
  } that I would like to be deleted.

The file follows a CSV format with the following columns:
- channel_id: The Discord channel ID
- message_id: The specific message ID to be deleted

Please process this request accordingly. If you need any additional information, please let me know.

Thank you for your assistance.

Best regards,
[Your Name]`;

  return baseTemplate.trim();
}

function getSelectedData(channels: ProcessedChannel[]): ProcessedChannel[] {
  const selectedMessages = useSelection.getState().selectedMessages;
  return channels.filter((channel) => selectedMessages.has(channel.id));
}

function formatDataForCSV(selectedData: ProcessedChannel[]) {
  const result: { channel_id: string; message_id: string }[] = [];
  const selectedMessages = useSelection.getState().selectedMessages;

  for (const channel of selectedData) {
    const selectedMessageIds = selectedMessages.get(channel.id);
    if (!selectedMessageIds) continue;

    const messageArray = Array.from(selectedMessageIds);
    const chunkResult = messageArray.map((messageId) => ({
      channel_id: channel.id,
      message_id: messageId,
    }));

    result.push(...chunkResult);
  }

  return result;
}

function generateExportJSON(
  selectedData: ProcessedChannel[],
  includeContent: boolean,
) {
  const selectedMessages = useSelection.getState().selectedMessages;

  if (!includeContent) {
    const result: Record<
      string,
      {
        messageIds: string[];
        displayName: string;
        serverName?: string;
        channelType: string;
      }
    > = {};

    for (const channel of selectedData) {
      const selectedMessageIds = selectedMessages.get(channel.id);
      if (!selectedMessageIds || selectedMessageIds.size === 0) continue;

      result[channel.id] = {
        messageIds: Array.from(selectedMessageIds),
        displayName: channel.displayName,
        serverName: channel.serverName,
        channelType: channel.type,
      };
    }

    return result;
  } else {
    const result: Record<
      string,
      {
        displayName: string;
        serverName?: string;
        channelType: string;
        messages: {
          id: string;
          timestamp: string;
          content: string;
          attachments: {
            url: string;
            filename: string;
          }[];
        }[];
      }
    > = {};

    for (const channel of selectedData) {
      const selectedMessageIds = selectedMessages.get(channel.id);
      if (!selectedMessageIds || selectedMessageIds.size === 0) continue;

      const selectedIdSet = new Set(selectedMessageIds);

      const fullMessages = channel.messages
        .filter((msg) => selectedIdSet.has(msg.ID))
        .map((msg) => ({
          id: msg.ID,
          timestamp: msg.Timestamp,
          content: msg.Contents,
          attachments: msg.parsedAttachments || [],
        }));

      if (fullMessages.length === 0) continue;

      result[channel.id] = {
        displayName: channel.displayName,
        serverName: channel.serverName,
        channelType: channel.type,
        messages: fullMessages,
      };
    }

    return result;
  }
}

function downloadFile(content: string, filename: string, type: string): void {
  const blob = new Blob([content], { type });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");

  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export function ExportModal({ open, onClose, channels }: ExportModalProps) {
  const [flow, setFlow] = useState<Flow | null>(null);
  const [step, setStep] = useState(1);
  const [platform, setPlatform] = useState<Platform>(() => detectOS());
  const [includeMessageContent, setIncludeMessageContent] = useState(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const selectedMessages = useSelection((state) => state.selectedMessages);
  const selectedData = useMemo(
    () => getSelectedData(channels),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [channels, selectedMessages],
  );

  const deletionSteps = [
    { title: "Open Form", component: OpenPrivacyForm },
    { title: "Generate Template", component: () => <GenerateTemplate /> },
    { title: "Fill Form", component: FillFormInstructions },
    {
      title: "Download Data",
      component: () => <DownloadCSV selectedData={selectedData} />,
    },
    { title: "Submit", component: ReviewAndSubmit },
    {
      title: "Complete",
      component: () => (
        <CompletionStep
          flow="deletion"
          onClose={handleClose}
          onFlowSelect={handleFlowSelect}
        />
      ),
    },
  ];

  const downloadSteps = [
    {
      title: "Download URLs",
      component: () => <DownloadURLList selectedData={selectedData} />,
    },
    {
      title: "Select Platform",
      component: () => (
        <PlatformSelector
          selectedPlatform={platform}
          onSelectPlatform={setPlatform}
        />
      ),
    },
    {
      title: "Install Dependencies",
      component: () => (
        <div className="mx-auto max-w-md space-y-6">
          <div className="space-y-2 text-center">
            <h3 className="text-xl font-semibold tracking-tight">
              Install Dependencies
            </h3>
            <p className="text-muted-foreground text-sm">
              Install required tools for building the downloader
            </p>
          </div>
          <DependencyInstructions platform={platform} />
        </div>
      ),
    },
    {
      title: "Build Tool",
      component: () => (
        <div className="mx-auto max-w-md space-y-6">
          <div className="space-y-2 text-center">
            <h3 className="text-xl font-semibold tracking-tight">
              Build the Downloader
            </h3>
            <p className="text-muted-foreground text-sm">
              Clone the repository and build the downloader
            </p>
          </div>
          <BuildInstructions platform={platform} />
        </div>
      ),
    },
    {
      title: "Run Tool",
      component: () => (
        <div className="mx-auto max-w-md space-y-6">
          <div className="space-y-2 text-center">
            <h3 className="text-xl font-semibold tracking-tight">
              Run the Downloader
            </h3>
            <p className="text-muted-foreground text-sm">
              Execute the downloader to save your attachments
            </p>
          </div>
          <RunInstructions platform={platform} />
        </div>
      ),
    },
    {
      title: "Complete",
      component: () => (
        <CompletionStep
          flow="download"
          onClose={handleClose}
          onFlowSelect={handleFlowSelect}
        />
      ),
    },
  ];

  const jsonSteps = [
    {
      title: "Export Options",
      component: () => (
        <JSONExportOptions
          onExportJSON={handleJSONExport}
          includeMessageContent={includeMessageContent}
          setIncludeMessageContent={setIncludeMessageContent}
        />
      ),
    },
    {
      title: "Complete",
      component: () => (
        <CompletionStep
          flow="json"
          onClose={handleClose}
          onFlowSelect={handleFlowSelect}
        />
      ),
    },
  ];

  const currentSteps =
    flow === "deletion"
      ? deletionSteps
      : flow === "download"
        ? downloadSteps
        : flow === "json"
          ? jsonSteps
          : [];

  const handleClose = () => {
    setFlow(null);
    setStep(1);
    onClose();
  };

  const handleFlowSelect = (newFlow: Flow) => {
    setFlow(newFlow);
    setStep(1);
  };

  const handleJSONExport = () => {
    const exportData = generateExportJSON(selectedData, includeMessageContent);
    const fileName = includeMessageContent
      ? "discorch_messages_full.json"
      : "discorch_messages.json";
    downloadFile(
      JSON.stringify(exportData, null, 2),
      fileName,
      "application/json",
    );
    setStep(2);
  };

  useEffect(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = 0;
    }
  }, [step, flow]);

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="flex max-h-[85vh] flex-col gap-0 p-0 sm:max-w-3xl">
        <DialogHeader className="border-b p-4">
          <DialogTitle className="text-lg font-semibold">
            {flow ? currentSteps[step - 1]?.title : "Export Selected Messages"}
          </DialogTitle>
          <DialogDescription className="mt-1.5 text-sm">
            {flow
              ? "Follow the steps below to complete your request"
              : "Review your selection and choose how to proceed"}
          </DialogDescription>
        </DialogHeader>

        <div
          ref={scrollContainerRef}
          className="max-h-[calc(85vh-4rem)] flex-1 space-y-8 overflow-y-auto p-6"
        >
          {!flow ? (
            <FlowSelector onSelectFlow={handleFlowSelect} />
          ) : (
            <>
              <StepIndicator
                currentStep={step}
                totalSteps={currentSteps.length}
              />
              {currentSteps[step - 1]?.component()}
            </>
          )}
        </div>

        <DialogFooter className="bg-muted/50 flex shrink-0 flex-col-reverse gap-2 border-t p-4 sm:flex-row sm:justify-between">
          <Button variant="ghost" onClick={handleClose} size="sm">
            Cancel
          </Button>

          {flow && (
            <div className="flex w-full gap-2 sm:w-auto">
              {step > 1 && (
                <Button
                  variant="outline"
                  onClick={() => setStep((s) => s - 1)}
                  className="flex-1 sm:w-28"
                  size="sm"
                >
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  Back
                </Button>
              )}
              {step < currentSteps.length && flow !== "json" && (
                <Button
                  onClick={() => setStep((s) => s + 1)}
                  className="flex-1 sm:w-28"
                  size="sm"
                >
                  Continue
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              )}
              {step === currentSteps.length && (
                <Button
                  onClick={handleClose}
                  className="flex-1 sm:w-28"
                  size="sm"
                >
                  Finish
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              )}
            </div>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
